import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { useNavigate } from "react-router-dom";
import { Zap, RefreshCw, ArrowUpRight, CheckCircle2, AlertTriangle, Info, AlertCircle, ChevronLeft } from "lucide-react";

const SEV = {
  high:   { icon: AlertCircle,   color: "#EF4444", bg: "#EF444412" },
  medium: { icon: AlertTriangle, color: "#F59E0B", bg: "#F59E0B12" },
  low:    { icon: Info,          color: "#5A90F5", bg: "#5A90F512" },
};
const TYPE_META = {
  execution:  { label: "Execution",  color: "#EF4444" },
  direction:  { label: "Direction",  color: "#F59E0B" },
  knowledge:  { label: "Knowledge",  color: "#5A90F5" },
  commitment: { label: "Commitment", color: "#A855F7" },
};
const TAB_PATHS = { commitments:"/commitments", boards:"/boards", capture:"/capture", ab:"/ab", compass:"/compass" };

export default function Tension() {
  const { current } = useWorkspace();
  const navigate = useNavigate();
  const [tensions, setTensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const wsId = current?.workspace_id;

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/tension`);
      setTensions(data.tensions || []);
    } catch {} finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  const highCount = tensions.filter(t=>t.severity==="high").length;
  const medCount = tensions.filter(t=>t.severity==="medium").length;

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A]">
      <div className="px-8 py-5 border-b border-[#1A1A1A] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="p-1.5 -ml-1.5 rounded-lg hover:bg-[#1a1a1a] text-[#71717A] hover:text-[#A1A1AA] transition-colors flex-shrink-0" title="Back to Overview">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 rounded-xl bg-[#EF4444]/10 flex items-center justify-center"><Zap className="w-4 h-4 text-[#EF4444]"/></div>
          <div>
            <h1 className="text-[18px] font-bold text-[#EDEDED]">Tension</h1>
            <p className="text-[11px] text-[#3A3A3A]">
              {tensions.length === 0 ? "No conflicts detected" : `${highCount > 0 ? `${highCount} high` : ""}${highCount > 0 && medCount > 0 ? ", " : ""}${medCount > 0 ? `${medCount} medium` : ""} severity`}
            </p>
          </div>
        </div>
        <button onClick={load} className="p-2 rounded-xl hover:bg-[#1a1a1a] text-[#52525B] hover:text-[#A1A1AA]">
          <RefreshCw className={`w-3.5 h-3.5 ${loading?"animate-spin":""}`}/>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12"><RefreshCw className="w-5 h-5 text-[#3A3A3A] animate-spin"/></div>
        ) : tensions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#111] border border-[#2A2A2A] flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-[#00C781]"/>
            </div>
            <div className="text-center">
              <p className="text-[14px] font-semibold text-[#00C781]">No tensions detected</p>
              <p className="text-[12px] text-[#3A3A3A] mt-1">Your system is consistent right now</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3 max-w-2xl">
            <p className="text-[11px] text-[#3A3A3A] mb-4">Crescendo found the following contradictions or friction points across your tabs.</p>
            {["high","medium","low"].map(sev => {
              const group = tensions.filter(t=>t.severity===sev);
              if (!group.length) return null;
              const { icon: Icon, color, bg } = SEV[sev];
              return (
                <div key={sev}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-2 capitalize" style={{color}}>{sev} severity</p>
                  {group.map(t => {
                    const tm = TYPE_META[t.type] || TYPE_META.execution;
                    const path = t.tab_a ? TAB_PATHS[t.tab_a] : null;
                    return (
                      <div key={t.tension_id} className="flex items-start gap-3 p-4 rounded-xl border mb-2 group" style={{background:bg,borderColor:color+"30"}}>
                        <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{color}}/>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-[#EDEDED]">{t.title}</p>
                          <p className="text-[11px] text-[#52525B] mt-1 leading-snug">{t.description}</p>
                          <span className="inline-block mt-2 text-[9px] px-2 py-0.5 rounded-full bg-[#1A1A1A] border border-[#2A2A2A]" style={{color:tm.color}}>{tm.label}</span>
                        </div>
                        {path && (
                          <button onClick={()=>navigate(path)} className="p-1.5 rounded-lg hover:bg-[#1a1a1a] text-[#3A3A3A] hover:text-[#A1A1AA] opacity-0 group-hover:opacity-100 flex-shrink-0">
                            <ArrowUpRight className="w-3.5 h-3.5"/>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
